import { useRef, useState } from 'react';
import { Copy, Eye, EyeOff, GripVertical, Plus, Trash2, Type } from 'lucide-react';
import clsx from 'clsx';
import { BLEND_MODES, TEXT_TECHNIQUE_ID, type Layer } from '../../store/layer';
import { useStudio } from '../../store/studioStore';
import { techniquesByCategory } from '../../techniques/registry';
import { CATEGORY_LABELS } from '../../techniques/types';
import { Slider } from '../controls/Slider';
import { Select } from '../controls/Basics';

export function LayerPanel(): JSX.Element {
  const layers = useStudio((s) => s.doc.layers);
  const activeLayerId = useStudio((s) => s.doc.activeLayerId);
  const selectLayer = useStudio((s) => s.selectLayer);
  const updateLayer = useStudio((s) => s.updateLayer);
  const removeLayer = useStudio((s) => s.removeLayer);
  const duplicateLayerById = useStudio((s) => s.duplicateLayerById);
  const moveLayer = useStudio((s) => s.moveLayer);
  const addLayer = useStudio((s) => s.addLayer);

  const [dragId, setDragId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);

  const categories = techniquesByCategory();
  const active = layers.find((l) => l.id === activeLayerId);

  // The list reads top-down like the visual stack, so index 0 (drawn first) is last.
  const displayed = [...layers].reverse();

  return (
    <div className="px-3 pb-3">
      <div className="flex items-center gap-1 py-2.5">
        <span className="panel-label flex-1">Layer</span>
        {categories.length > 0 && (
          <div ref={addRef} className="relative">
            <button
              type="button"
              className="ghost-button h-6 w-6 p-0"
              title="Technik als Layer hinzufügen"
              onClick={() => setAdding((v) => !v)}
            >
              <Plus size={13} />
            </button>
            {adding && (
              <div className="absolute right-0 top-7 z-50 max-h-[420px] w-[260px] overflow-y-auto rounded-lg border border-ink-700 bg-ink-850 py-1 shadow-2xl shadow-black/60">
                {categories.map(({ category, techniques }) => (
                  <div key={category}>
                    <div className="px-3 py-1 panel-label">{CATEGORY_LABELS[category]}</div>
                    {techniques.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          addLayer(t.id);
                          setAdding(false);
                        }}
                        className="block w-full px-3 py-1.5 text-left transition-colors hover:bg-ink-800"
                      >
                        <div className="text-[12px] text-ink-100">{t.name}</div>
                        <div className="text-[10px] leading-snug text-ink-500">{t.description}</div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-0.5">
        {displayed.map((layer, displayIndex) => {
          const realIndex = layers.length - 1 - displayIndex;
          const isText = layer.techniqueId === TEXT_TECHNIQUE_ID;
          return (
            <div
              key={layer.id}
              draggable
              onDragStart={() => setDragId(layer.id)}
              onDragEnd={() => {
                setDragId(null);
                setDropIndex(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragId && dragId !== layer.id) setDropIndex(realIndex);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId && dropIndex !== null) moveLayer(dragId, dropIndex);
                setDragId(null);
                setDropIndex(null);
              }}
              onClick={() => selectLayer(layer.id)}
              className={clsx(
                'group/layer cursor-pointer rounded-md border px-1.5 py-1.5 transition-colors',
                layer.id === activeLayerId
                  ? 'border-ink-600 bg-ink-800'
                  : 'border-transparent bg-ink-850/60 hover:bg-ink-850',
                dropIndex === realIndex && dragId !== layer.id && 'border-accent-500',
                dragId === layer.id && 'opacity-40',
              )}
            >
              <div className="flex items-center gap-1.5">
                <GripVertical size={11} className="shrink-0 cursor-grab text-ink-600" />
                {isText && <Type size={11} className="shrink-0 text-ink-400" />}
                <input
                  value={layer.name}
                  onChange={(e) => updateLayer(layer.id, { name: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  className="min-w-0 flex-1 truncate rounded border border-transparent bg-transparent px-1 py-0.5 text-[11px] text-ink-100 outline-none hover:border-ink-700 focus:border-ink-600 focus:bg-ink-900"
                />
                <button
                  type="button"
                  title={layer.visible ? 'Ausblenden' : 'Einblenden'}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateLayer(layer.id, { visible: !layer.visible });
                  }}
                  className={clsx('ghost-button h-5 w-5 p-0', !layer.visible && 'text-ink-600')}
                >
                  {layer.visible ? <Eye size={11} /> : <EyeOff size={11} />}
                </button>
                {!isText && (
                  <>
                    <button
                      type="button"
                      title="Duplizieren"
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateLayerById(layer.id);
                      }}
                      className="ghost-button h-5 w-5 p-0 opacity-0 group-hover/layer:opacity-100"
                    >
                      <Copy size={11} />
                    </button>
                    <button
                      type="button"
                      title="Löschen"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeLayer(layer.id);
                      }}
                      className="ghost-button h-5 w-5 p-0 opacity-0 hover:text-red-400 group-hover/layer:opacity-100"
                    >
                      <Trash2 size={11} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {active && (
        <div className="mt-2 rounded-md bg-ink-850/60 px-2 py-1.5">
          <Slider
            label="Deckkraft"
            value={active.opacity}
            onChange={(opacity) => updateLayer(active.id, { opacity })}
            min={0}
            max={1}
            step={0.01}
            defaultValue={1}
          />
          <div className="flex items-center gap-2 py-1">
            <span className="w-[76px] shrink-0 text-[11px] text-ink-300">Blend</span>
            <div className="min-w-0 flex-1">
              <Select
                value={active.blendMode}
                onChange={(blendMode) => updateLayer(active.id, { blendMode })}
                options={BLEND_MODES}
              />
            </div>
          </div>
          {active.techniqueId !== TEXT_TECHNIQUE_ID && <LayerSeed layer={active} />}
        </div>
      )}
    </div>
  );
}

function LayerSeed({ layer }: { layer: Layer }): JSX.Element {
  const updateLayer = useStudio((s) => s.updateLayer);
  return (
    <Slider
      label="Layer-Seed"
      value={layer.seed}
      onChange={(seed) => updateLayer(layer.id, { seed: Math.round(seed) })}
      min={0}
      max={999999999}
      step={1}
      locked={layer.seedLocked}
      onToggleLock={() => updateLayer(layer.id, { seedLocked: !layer.seedLocked })}
      onRandomize={() => updateLayer(layer.id, { seed: Math.floor(Math.random() * 999999999) })}
      tooltip="Eigener Zufallsstrom dieses Layers — Änderungen an anderen Layern bleiben ohne Wirkung."
      precision={0}
    />
  );
}
